"use client"

import { useState, useEffect } from "react"
import { Gift, Check, Users, Crown, Loader2, Mail, Send, X } from "lucide-react"
import { useGetReferralInfoQuery, useInviteFriendsMutation } from "@/app/store/api/authApi"
import { useAppSelector } from "@/app/store/hooks"
import { toast } from "sonner"
import { Modal } from "@/components/ui/modal"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'

const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
    })
}

export function ReferralTab() {
    const { token, isAuthenticated } = useAppSelector((state) => state.auth)
    const { data, isLoading, error, refetch } = useGetReferralInfoQuery(undefined, {
        skip: !isAuthenticated, // Skip if not authenticated
    })
    const [inviteFriends, { isLoading: isInviting }] = useInviteFriendsMutation()
    const [showInviteModal, setShowInviteModal] = useState(false)
    const [inviteEmail, setInviteEmail] = useState("")
    const [emailChips, setEmailChips] = useState<string[]>([])

    // Debug logging
    useEffect(() => {
        console.log('ReferralTab Debug:', {
            isAuthenticated,
            hasToken: !!token,
            data,
            error,
            isLoading,
            showInviteModal
        })
    }, [isAuthenticated, token, data, error, isLoading, showInviteModal])

    // Refetch when authentication becomes available
    useEffect(() => {
        if (isAuthenticated && !data && !isLoading && !error) {
            refetch()
        }
    }, [isAuthenticated, data, isLoading, error, refetch])

    const referralCode = data?.referralCode || ""
    const referrals = data?.referrals || []

    // Calculate stats
    const totalReferred = referrals.length
    const completedReferrals = referrals.filter(r => r.status === "COMPLETED").length
    const pendingReferrals = referrals.filter(r => r.status === "PENDING").length
    const totalDecksEarned = referrals.reduce((sum, r) => sum + (r.status === "COMPLETED" ? (r.decksReferrer || 0) : 0), 0)

    const handleShareLink = async () => {
        const shareUrl = `${window.location.origin}/signup?code=${referralCode}`
        try {
            await navigator.clipboard.writeText(shareUrl)
            toast.success("Referral link copied!")
        } catch {
            toast.error("Failed to copy link")
        }
    }

    const isValidEmail = (email: string) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    }

    const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            const email = inviteEmail.trim()
            if (!email) return

            if (!isValidEmail(email)) {
                toast.error("Please enter a valid email address")
                return
            }

            if (emailChips.includes(email)) {
                toast.error("This email is already added")
                return
            }

            if (emailChips.length >= 10) {
                toast.error("Maximum 10 emails allowed at once")
                return
            }

            setEmailChips([...emailChips, email])
            setInviteEmail("")
        }
    }

    const removeEmailChip = (emailToRemove: string) => {
        setEmailChips(emailChips.filter(email => email !== emailToRemove))
    }

    const handleInvite = async () => {
        if (emailChips.length === 0) {
            toast.error("Please add at least one email address")
            return
        }

        try {
            await inviteFriends({ emails: emailChips }).unwrap()
            toast.success(`Invitation${emailChips.length > 1 ? 's' : ''} sent successfully!`)
            setEmailChips([])
            setInviteEmail("")
            setShowInviteModal(false)
        } catch (error: any) {
            toast.error(error?.data?.message || "Failed to send invitations")
        }
    }

    const handleCloseInviteModal = () => {
        if (!isInviting) {
            setShowInviteModal(false)
            setEmailChips([])
            setInviteEmail("")
        }
    }

    // Show loading state
    if (isLoading || (!data && !error && isAuthenticated)) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
        )
    }

    // Show error state
    if (error) {
        const errorMessage = (error as any)?.data?.message || "Failed to load referral information"
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
                <p className="text-sm text-red-500">{errorMessage}</p>
                <p className="text-xs text-slate-400">Please try again later</p>
                <button
                    onClick={() => refetch()}
                    className="mt-2 px-4 py-2 text-xs font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                >
                    Try Again
                </button>
            </div>
        )
    }

    // Show auth required state
    if (!isAuthenticated) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
                <p className="text-sm text-slate-500">Please log in to view your referral information</p>
            </div>
        )
    }

    return (
        <div className="space-y-6 max-w-4xl mx-auto">
            {/* Referral Code Card */}
            <div className="bg-gradient-to-br from-slate-50 to-blue-50/50 rounded-xl border border-slate-100 p-4 md:p-6">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="text-sm font-bold text-slate-900 mb-1">Your Referral Code</h3>
                        <p className="text-xs text-slate-500">Share this code with friends to earn free fast decks</p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                        <Gift className="w-5 h-5" />
                    </div>
                </div>

                <div className="bg-white rounded-lg border border-blue-200 border-dashed px-4 py-3 flex items-center justify-between">
                    <span className="flex-1 font-mono text-lg font-bold text-blue-700 tracking-wider text-center">
                        {referralCode}
                    </span>
                    <button
                        onClick={() => {
                            navigator.clipboard.writeText(referralCode)
                            toast.success("Referral code copied!")
                        }}
                        className="ml-2 p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                        title="Copy code"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
                            <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
                        </svg>
                    </button>
                </div>

                <div className="mt-3 flex gap-2">
                    <button
                        onClick={handleShareLink}
                        className="flex-1 py-2 text-xs font-semibold text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200"
                    >
                        Copy shareable link
                    </button>
                    <button
                        onClick={() => {
                            console.log('Invite via Email clicked, setting showInviteModal to true')
                            setShowInviteModal(true)
                        }}
                        className="flex-1 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                    >
                        <Mail className="w-3.5 h-3.5" />
                        Invite via Email
                    </button>
                </div>

                {/* Reward Info */}
                <div className="mt-4 pt-4 grid grid-cols-2 gap-4 text-center">
                    <div>
                        <div className="text-2xl font-bold text-blue-600">1</div>
                        <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Free Fast Deck for you</div>
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-blue-600">1</div>
                        <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">Free Fast Deck for friend</div>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 md:gap-3">
                <div className="flex items-center gap-2 md:gap-3 p-2.5 md:p-4 bg-slate-50/80 rounded-xl">
                    <div className="w-7 h-7 md:w-9 md:h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                        <Users className="w-3.5 h-3.5 md:w-4 md:h-4" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-lg md:text-2xl font-bold text-slate-900">{totalReferred}</div>
                        <div className="text-[8px] md:text-[10px] font-medium text-slate-400 uppercase tracking-wide">Referred</div>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-3 p-2.5 md:p-4 bg-slate-50/80 rounded-xl">
                    <div className="w-7 h-7 md:w-9 md:h-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                        <Check className="w-3.5 h-3.5 md:w-4 md:h-4" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-lg md:text-2xl font-bold text-slate-900">{completedReferrals}</div>
                        <div className="text-[8px] md:text-[10px] font-medium text-slate-400 uppercase tracking-wide">Completed</div>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-3 p-2.5 md:p-4 bg-slate-50/80 rounded-xl">
                    <div className="w-7 h-7 md:w-9 md:h-9 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 shrink-0">
                        <Crown className="w-3.5 h-3.5 md:w-4 md:h-4" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-lg md:text-2xl font-bold text-slate-900">{totalDecksEarned}</div>
                        <div className="text-[8px] md:text-[10px] font-medium text-slate-400 uppercase tracking-wide">Decks Earned</div>
                    </div>
                </div>
            </div>

            {/* Referral History */}
            <div>
                <div className="flex items-center justify-between mb-4 px-1">
                    <h3 className="font-bold text-sm text-slate-900">Referral History</h3>
                    {pendingReferrals > 0 && (
                        <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                            {pendingReferrals} pending
                        </span>
                    )}
                </div>

                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e2e8f0', borderRadius: '0.75rem' }}>
                    <Table sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', tableLayout: 'fixed' }} aria-label="referral history table">
                        <colgroup>
                            <col style={{ width: '40%' }} />
                            <col style={{ width: '30%' }} />
                            <col style={{ width: '30%' }} />
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontFamily: 'inherit', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.5, pl: 3 }}>Friend</TableCell>
                                <TableCell align="center" sx={{ fontFamily: 'inherit', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.5 }}>Joined</TableCell>
                                <TableCell align="right" sx={{ fontFamily: 'inherit', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.5, pr: 3 }}>Decks</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {referrals.length > 0 ? (
                                referrals.map((referral) => {
                                    // Handle case where refereeId might be a string or populated object
                                    const referee = typeof referral.refereeId === 'object' ? referral.refereeId : null;
                                    const refereeName = referee ? `${referee.firstName} ${referee.lastName || ''}`.trim() : 'Unknown User';
                                    const refereeEmail = referee?.email || '';

                                    return (
                                    <TableRow
                                        key={referral._id}
                                        sx={{
                                            '&:last-child td, &:last-child th': { border: 0 },
                                            '&:hover': { backgroundColor: '#f8fafc' }
                                        }}
                                    >
                                        <TableCell component="th" scope="row" sx={{ fontFamily: 'inherit', py: 2, pl: 3, borderColor: '#f1f5f9' }}>
                                            <div>
                                                <div className="text-[13px] font-bold text-slate-700">
                                                    {refereeName}
                                                </div>
                                                <div className="text-[11px] text-slate-400">{refereeEmail}</div>
                                            </div>
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontFamily: 'inherit', fontSize: '12px', fontWeight: 500, color: '#64748b', py: 2, borderColor: '#f1f5f9' }}>
                                            {formatDate(referral.createdAt)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'inherit', py: 2, pr: 3, borderColor: '#f1f5f9' }}>
                                            <div className="flex items-center justify-end gap-1">
                                                <span className={`text-[13px] font-bold ${referral.status === "COMPLETED" ? "text-emerald-600" : "text-slate-300"}`}>
                                                    {referral.status === "COMPLETED" ? `+${referral.decksReferrer || 0}` : "—"}
                                                </span>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={3} sx={{ fontFamily: 'inherit', py: 6, textAlign: 'center', color: '#94a3b8' }}>
                                        <div className="flex flex-col items-center gap-2">
                                            <Users className="w-8 h-8 text-slate-300" />
                                            <p className="text-sm">No referrals yet</p>
                                            <p className="text-xs text-slate-400">Share your code to start earning free decks!</p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            {/* Invite Modal - zIndex must be higher than Settings Modal close button (z-100) */}
            <Modal
                isOpen={showInviteModal}
                onClose={handleCloseInviteModal}
                className="max-w-md p-0 overflow-hidden"
                zIndex={110}
            >
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-5 text-center">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3">
                        <Mail className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-white">Invite Friends</h3>
                    <p className="text-blue-100 text-xs mt-1">Send invitation emails with your referral link</p>
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <label htmlFor="invite-email" className="block text-xs font-semibold text-slate-600 mb-1.5">
                            Enter email addresses
                        </label>

                        {/* Email chips container */}
                        <div className="min-h-[44px] p-2 border border-slate-200 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all">
                            <div className="flex flex-wrap gap-2">
                                {emailChips.map((email) => (
                                    <div
                                        key={email}
                                        className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full text-xs font-medium"
                                    >
                                        <span>{email}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeEmailChip(email)}
                                            className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                                            disabled={isInviting}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                                <input
                                    id="invite-email"
                                    type="email"
                                    placeholder={emailChips.length === 0 ? "friend@example.com" : "Add another..."}
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    onKeyDown={handleEmailKeyDown}
                                    className="flex-1 min-w-[150px] outline-none text-sm py-1 bg-transparent"
                                    disabled={isInviting}
                                />
                            </div>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1.5">Press Enter to add each email (max 10)</p>
                    </div>

                    <div className="bg-blue-50 rounded-lg px-3 py-2.5 text-xs text-slate-600">
                        <p>Your friends will receive:</p>
                        <ul className="mt-1 space-y-0.5 text-blue-700 font-medium">
                            <li>• A personalized invitation from you</li>
                            <li>• 1 free fast deck when they sign up</li>
                        </ul>
                    </div>

                    <div className="flex gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCloseInviteModal}
                            disabled={isInviting}
                            className="flex-1"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleInvite}
                            disabled={isInviting || emailChips.length === 0}
                            className="flex-1 bg-blue-600 hover:bg-blue-700"
                        >
                            {isInviting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <Send className="w-4 h-4 mr-2" />
                                    Send {emailChips.length > 0 ? `(${emailChips.length})` : 'Invite'}
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    )
}
